/**
 * PostMeetingNotes — quick-chip + free-text "what surprised you?" form.
 * Per Feature #2 §post-meeting notes loop. The stickiness multiplier.
 *
 * Renders below a Pre-Meeting Blueprint after the meeting time has passed.
 * Logs to /api/post-meeting-notes which fans out to observed-behavior tags +
 * manager-private notes on each attendee Profile.
 */

import { useState } from "react";
import { Callout, Chip } from "./index.js";

const QUICK_CHIPS = [
  { value: "went_well",      label: "Went well" },
  { value: "tense",          label: "Tense" },
  { value: "pushed_back",    label: "Pushed back on X" },
  { value: "shut_down",      label: "Shut down" },
  { value: "got_emotional",  label: "Got emotional" },
  { value: "accepted",       label: "Accepted" },
  { value: "declined",       label: "Declined" },
  { value: "needs_followup", label: "Needs follow-up" },
];

export default function PostMeetingNotes({ blueprintId, attendeeProfileIds, bearerToken, onSaved }) {
  const [selected, setSelected] = useState(new Set());
  const [surprise, setSurprise] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const toggle = (chip) => {
    const next = new Set(selected);
    if (next.has(chip)) next.delete(chip);
    else next.add(chip);
    setSelected(next);
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch("/.netlify/functions/post-meeting-notes", {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${bearerToken}` },
        body: JSON.stringify({
          blueprint_id: blueprintId,
          attendee_profile_ids: attendeeProfileIds,
          quick_chips: [...selected],
          surprise_text: surprise,
        }),
      });
      const body = await res.json();
      if (!res.ok) {
        setError(body.error || "Save failed");
      } else {
        setSaved(true);
        if (onSaved) onSaved(body);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <Callout.Tip>
        <strong>Logged.</strong> This person's living Playbook just got tighter. Your next Pre-Meeting Brief with them will reflect what you logged.
      </Callout.Tip>
    );
  }

  return (
    <div
      style={{
        marginTop: 32,
        padding: "20px 22px",
        border: "1px solid var(--ppr-color-border-default)",
        borderTop: "8px solid var(--ppr-color-accent-coral)",
        background: "var(--ppr-color-background-surface)",
      }}
      data-component="PostMeetingNotes"
    >
      <div
        style={{
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "16px",
          letterSpacing: "var(--ppr-typography-letter-spacing-wide)",
          textTransform: "uppercase",
          color: "var(--ppr-color-accent-navy)",
          marginBottom: 12,
        }}
      >
        How did the meeting go?
      </div>

      <div
        style={{
          fontFamily: "var(--ppr-typography-font-family-body)",
          fontSize: "var(--ppr-typography-font-size-base)",
          color: "var(--ppr-color-text-secondary)",
          lineHeight: "var(--ppr-typography-line-height-relaxed)",
          marginBottom: 14,
        }}
      >
        60 seconds. The data tightens the model on this person, this dynamic, this kind of conversation.
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
        {QUICK_CHIPS.map((c) => (
          <Chip key={c.value} primary={selected.has(c.value)} onClick={() => toggle(c.value)}>
            {c.label}
          </Chip>
        ))}
      </div>

      <label style={{
        display: "block",
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
        textTransform: "uppercase",
        color: "var(--ppr-color-text-secondary)",
        marginBottom: 6,
      }}>
        What surprised you?
      </label>
      <textarea
        value={surprise}
        onChange={(e) => setSurprise(e.target.value)}
        rows={3}
        placeholder="Anything that didn't match the prep…"
        style={{
          width: "100%",
          padding: "12px 14px",
          fontFamily: "var(--ppr-typography-font-family-body)",
          fontSize: "var(--ppr-typography-font-size-base)",
          lineHeight: "var(--ppr-typography-line-height-relaxed)",
          border: "1px solid var(--ppr-color-border-default)",
          background: "var(--ppr-color-background-surface)",
          color: "var(--ppr-color-text-primary)",
          resize: "vertical",
          outline: "none",
        }}
      />

      {error && (
        <div style={{ marginTop: 12, color: "var(--ppr-color-accent-coral-dark)", fontSize: "14px" }}>
          {error}
        </div>
      )}

      <button
        onClick={submit}
        disabled={saving || (selected.size === 0 && !surprise.trim())}
        style={{
          marginTop: 16,
          padding: "12px 18px",
          fontFamily: "var(--ppr-typography-font-family-display)",
          fontSize: "var(--ppr-typography-font-size-sm)",
          letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
          textTransform: "uppercase",
          border: "1px solid var(--ppr-color-border-default)",
          background: "var(--ppr-color-accent-navy)",
          color: "var(--ppr-color-text-inverse)",
          cursor: saving ? "wait" : "pointer",
          opacity: saving || (selected.size === 0 && !surprise.trim()) ? 0.6 : 1,
        }}
      >
        {saving ? "Saving…" : "Log it"}
      </button>
    </div>
  );
}
